/**
 * Feb 2026 R293zw · Workspace Session Timeout settings card.
 *
 * Feb 2026 R293zze · Two separate windows — Web and Mobile — so admins
 * can keep laptops locking tight (e.g. 60 min) while giving mobile
 * apps a 30-day or no-logout window. Mobile defers to the web value
 * when left unset, so existing deployments keep today's behaviour.
 *
 * Owners / admins pick each window; the backend uses the strictest
 * finite value across the user's memberships at login + refresh.
 * "No auto-logout" maps to a 30-day ceiling.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Clock3, Save, Monitor, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const WEB_OPTIONS = [
  { value: "unset",  label: "Default (60 minutes)",   hint: "Workspace admin hasn't opted in" },
  { value: "15",     label: "15 minutes" },
  { value: "30",     label: "30 minutes" },
  { value: "60",     label: "1 hour" },
  { value: "120",    label: "2 hours" },
  { value: "240",    label: "4 hours" },
  { value: "480",    label: "8 hours" },
  { value: "720",    label: "12 hours" },
  { value: "1440",   label: "24 hours" },
  { value: "43200",  label: "No auto-logout (30 days)", hint: "Users stay signed in until they explicitly log out" },
];

// Mobile-first options — longer windows up top since that's the common
// mobile-app expectation (notifications, offline-first). "unset" means
// "same as web" so admins can keep one policy if they want.
const MOBILE_OPTIONS = [
  { value: "unset",  label: "Same as web", hint: "Mobile uses the window above" },
  { value: "60",     label: "1 hour" },
  { value: "240",    label: "4 hours" },
  { value: "1440",   label: "24 hours" },
  { value: "43200",  label: "30 days", hint: "Recommended for native apps" },
];

export default function SessionTimeoutSection() {
  const { activeWorkspaceId, memberships } = useAuth();
  // Feb 2026 R293zze · role lives on the membership, not on the
  // workspace. Reading `activeWorkspace?.role` always returned undefined
  // so the section was hidden for everyone. Fixed to read the role
  // from the active membership.
  const activeRole = useMemo(
    () => memberships?.find((m) => m.workspace_id === activeWorkspaceId)?.role,
    [memberships, activeWorkspaceId],
  );
  const isAdminish = ["owner", "admin", "workspace_owner", "workspace_admin", "super_admin"]
    .includes(activeRole);
  const [web, setWeb] = useState("unset");
  const [mobile, setMobile] = useState("unset");
  const [initialWeb, setInitialWeb] = useState("unset");
  const [initialMobile, setInitialMobile] = useState("unset");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const toKey = (v) => (v == null ? "unset" : String(v));

  useEffect(() => {
    if (!activeWorkspaceId) return;
    (async () => {
      try {
        const r = await api.get(`/workspaces/${activeWorkspaceId}/session-timeout`);
        const w = toKey(r.data?.session_timeout_minutes);
        const m = toKey(r.data?.session_timeout_mobile_minutes);
        setWeb(w); setInitialWeb(w);
        setMobile(m); setInitialMobile(m);
      } catch (e) {
        // Non-blocking — the setting is optional
      } finally {
        setLoading(false);
      }
    })();
  }, [activeWorkspaceId]);

  const save = async () => {
    if (!activeWorkspaceId) return;
    setSaving(true);
    try {
      const payload = {
        session_timeout_minutes: web === "unset" ? null : parseInt(web, 10),
        session_timeout_mobile_minutes: mobile === "unset" ? null : parseInt(mobile, 10),
      };
      await api.patch(`/workspaces/${activeWorkspaceId}/session-timeout`, payload);
      setInitialWeb(web);
      setInitialMobile(mobile);
      toast.success("Session timeout updated. New policy applies on next sign-in.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (!isAdminish) return null;

  const dirty = web !== initialWeb || mobile !== initialMobile;

  return (
    <section
      className="rounded-md border border-border bg-card p-4 space-y-3"
      data-testid="session-timeout-section"
    >
      <div className="flex items-center gap-2">
        <Clock3 className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Session timeout</h3>
      </div>
      <p className="text-xs text-muted-foreground">
        Everyone on this workspace is signed out automatically after this long with no activity.
        For users on several workspaces, the strictest window wins. Mobile apps can keep a longer
        window than the web.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium mb-1">
            <Monitor className="w-3.5 h-3.5 text-muted-foreground" />
            Web
          </div>
          <Select value={web} onValueChange={setWeb} disabled={loading}>
            <SelectTrigger className="h-9" data-testid="session-timeout-select-web">
              <SelectValue placeholder="Pick a window" />
            </SelectTrigger>
            <SelectContent>
              {WEB_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  <div className="flex flex-col">
                    <span>{o.label}</span>
                    {o.hint && (
                      <span className="text-[10px] text-muted-foreground">{o.hint}</span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium mb-1">
            <Smartphone className="w-3.5 h-3.5 text-muted-foreground" />
            Mobile app
          </div>
          <Select value={mobile} onValueChange={setMobile} disabled={loading}>
            <SelectTrigger className="h-9" data-testid="session-timeout-select-mobile">
              <SelectValue placeholder="Pick a window" />
            </SelectTrigger>
            <SelectContent>
              {MOBILE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  <div className="flex flex-col">
                    <span>{o.label}</span>
                    {o.hint && (
                      <span className="text-[10px] text-muted-foreground">{o.hint}</span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="session-timeout-save"
          size="sm"
        >
          <Save className="w-4 h-4 mr-1" />
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
      <div className="text-[11px] text-muted-foreground">
        Mobile apps are detected via the <code>X-Client: mobile</code> request header
        (and iOS / Android user-agents as a fallback). Change takes effect on each
        user's next sign-in or token refresh.
      </div>
    </section>
  );
}
